import { SuiMessageTypeType } from '@developers/scoro-ui';
import { useMessagesStore } from '../stores';
import { DEFAULT_TIMEOUT_SUCCESS } from '../constants';
import { type MessageType } from '../types';
import { type LiterallyAnything } from '@/types/utils';

/**
 * A composable to detach our current messages store from where we need to actually add and remove messages
 *
 * Usage example:
 * ```ts
 * const { addMessage } = useMessage();
 *
 * const msg: MessageType = {
 *   type: MessageType.Success,
 *   message: 'Something something dark side',
 * };
 * addMessage(msg);
 * ```
 */
export const useMessage = () => {
	const store = useMessagesStore();

	const addMessage = (message: MessageType) => {
		store.addMessage(message);
	};

	const removeMessage = (messageId: MessageType['id']) => {
		store.deleteMessage(messageId);
	};

	const addSuccessMessage = (
		message: Pick<MessageType, 'message' | 'title'>
	) => {
		const messageToAdd = {
			type: SuiMessageTypeType.Success,
			message: message.message,
			title: message.title,
			timeout: DEFAULT_TIMEOUT_SUCCESS,
		};
		store.addMessage(messageToAdd);
	};

	const addInfoMessage = (
		message: Pick<MessageType, 'message' | 'title' | 'timeout'>
	) => {
		const messageToAdd = {
			type: SuiMessageTypeType.Info,
			message: message.message,
			title: message.title,
			timeout: message.timeout || 5000,
		};
		store.addMessage(messageToAdd);
	};

	const addWarningMessage = (
		message: Pick<MessageType, 'message' | 'title'>
	) => {
		const messageToAdd = {
			type: SuiMessageTypeType.Warning,
			message: message.message,
			title: message.title,
		};
		store.addMessage(messageToAdd);
	};

	const addErrorMessage = (
		message: Pick<MessageType, 'message' | 'title'>
	) => {
		const messageToAdd = {
			type: SuiMessageTypeType.Error,
			message: message.message,
			title: message.title,
		};
		store.addMessage(messageToAdd);
	};

	const addMessageToSessionStorage = (message: MessageType) => {
		const messagesInSessionStorage = sessionStorage.getItem('messages');
		const type = message.type;
		let timeout = message.timeout;
		let messages: {
			[key in SuiMessageTypeType]?: {
				message: string;
				timeout: number | undefined;
				customMessageComponent?: string;
				customMessageProps?: LiterallyAnything;
			}[];
		} = {};

		if (messagesInSessionStorage) {
			messages = JSON.parse(messagesInSessionStorage);
		}

		if (type === SuiMessageTypeType.Success && !timeout) {
			timeout = DEFAULT_TIMEOUT_SUCCESS;
		}

		const messageObject = {
			message: message.message,
			timeout,
			customMessageProps: message.customMessageProps,
			customMessageComponent: message.customMessageComponent,
		};

		if (!messages[type]) {
			messages[type] = [];
		}

		messages[type]?.push(messageObject);
		sessionStorage.setItem('messages', JSON.stringify(messages));
	};

	return {
		addMessage,
		removeMessage,
		addSuccessMessage,
		addInfoMessage,
		addWarningMessage,
		addErrorMessage,
		addMessageToSessionStorage,
		DEFAULT_TIMEOUT_SUCCESS,
	};
};
